import React from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import delv from 'dlv';
import styled from 'styled-components';

import {
  ICombo,
  IItem,
  IItemOption,
  IModifierSelection,
  IQuickConfig,
  ISanityItemOption,
  ISanityItemOptionModifier,
} from '@rbi-ctg/menu';
import { logProductModifierCustomization } from 'components/product-detail/modifier/utils';
import SelectionContainer from 'components/selection-container';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { filterExcludedItemOptions } from 'utils/menu';
import { getItemCalories } from 'utils/menu/calories';
import {
  ModifierComponentStyle,
  getBooleanSelectionIndex,
  getModifierSelectionsText,
  getSelectorSelectionIndex,
  getStepperSelectionIndex,
  groupItemOptionsUsingDisplayGroups,
  isModifierSelected,
  modifierSelectedQuantity,
  shouldDisplayItemOption,
  shouldDisplayItemOptionGroup,
} from 'utils/menu/modifiers';

import { MenuOptionBoolean } from './menu-option-boolean';
import MenuOptionSelector from './menu-option-selector';
import MenuOptionStepper from './menu-option-stepper';
import { HandleModifierSelection, HandleQuantityUpdate } from './types';

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

interface IModifiersProps {
  comboSlotId?: string;
  item: ICombo | IItem | null;
  modifiers: (ISanityItemOption | IItemOption)[] | null;
  modifierSelections: IModifierSelection[];
  excludesForPickerAspects?: { value: string }[];
  radioGroupParentId: string;
  quickConfigs?: IQuickConfig[];
  handleModifierSelection: HandleModifierSelection;
  handleModifierQuantityUpdate: HandleQuantityUpdate;
}

function Modifiers({
  comboSlotId,
  item,
  modifiers,
  excludesForPickerAspects = [],
  radioGroupParentId,
  modifierSelections,
  quickConfigs,
  handleModifierSelection,
  handleModifierQuantityUpdate,
}: IModifiersProps) {
  const { priceForItemOptionModifier, showStaticMenu } = useMenuContext();
  const filterExcluded = filterExcludedItemOptions(excludesForPickerAspects);
  const formatCalories = useFormatCalories();

  const mParticle = useMParticleContext();
  const handleModifierChange = (
    itemId: string,
    modifier: ISanityItemOptionModifier | null,
    itemOption: ISanityItemOption,
    comboSlotId?: string
  ) => {
    logProductModifierCustomization(
      modifier?.name.locale ?? '',
      itemOption.name.locale,
      modifier?._key ?? '',
      mParticle
    );
    handleModifierSelection(itemId, modifier, itemOption, comboSlotId);
  };

  return (
    <>
      {groupItemOptionsUsingDisplayGroups(filterExcluded(modifiers || [])).map(group => {
        const selectedSelectorOptionModifierIndex = getSelectorSelectionIndex(
          group,
          modifierSelections,
          comboSlotId
        );

        const options: ISanityItemOption[] = delv(group, 'options') || [];
        const selectedSelectorOptionModifierName =
          selectedSelectorOptionModifierIndex >= 0
            ? delv(options, `0.options.${selectedSelectorOptionModifierIndex}.name.locale`, '')
            : getModifierSelectionsText(options, modifierSelections, quickConfigs);

        const isModifierRequired = () =>
          options.every(
            option =>
              (!option.componentStyle ||
                option.componentStyle === ModifierComponentStyle.Selector) &&
              option.minAmount > 0
          );

        return shouldDisplayItemOptionGroup(options) ? (
          <SelectionContainer
            title={group.title}
            key={group.title}
            selection={selectedSelectorOptionModifierName}
            required={!showStaticMenu && isModifierRequired()}
          >
            <VisuallyHidden>{group.title}</VisuallyHidden>
            <Form data-testid="modifier-detail" role="group" aria-label={group.title}>
              {group.options.map((option: ISanityItemOption) => {
                if (!shouldDisplayItemOption(option)) {
                  return null;
                }
                if (option.componentStyle === ModifierComponentStyle.Stepper) {
                  const selectionIndex = getStepperSelectionIndex(
                    option,
                    modifierSelections,
                    comboSlotId
                  );
                  const modifier = option.options[selectionIndex];

                  if (!modifier) {
                    return null;
                  }

                  const optionName = delv(option, 'name.locale');
                  const calories = getItemCalories(modifier);
                  const caloriesString = formatCalories(calories);
                  const modifierImage =
                    modifier.image ?? modifier.modifierMultiplier.modifier.image;

                  return (
                    <MenuOptionStepper
                      key={optionName}
                      name={optionName}
                      calories={caloriesString}
                      itemOption={option}
                      index={selectionIndex}
                      onChange={index => {
                        handleModifierChange(
                          radioGroupParentId,
                          option.options[index],
                          option,
                          comboSlotId
                        );
                      }}
                      price={priceForItemOptionModifier({ item, itemOption: option, modifier })}
                      image={modifierImage}
                      imageDescription={modifier?.imageDescription?.locale}
                    />
                  );
                } else if (option.componentStyle === ModifierComponentStyle.Boolean) {
                  const selectionIndex = getBooleanSelectionIndex(
                    option,
                    modifierSelections,
                    comboSlotId
                  );
                  const modifier = option.options[selectionIndex];

                  if (!modifier) {
                    return null;
                  }

                  const optionName = delv(option, 'name.locale');
                  return (
                    <MenuOptionBoolean
                      key={optionName}
                      index={selectionIndex}
                      options={option.options}
                      onChange={index => {
                        handleModifierChange(
                          radioGroupParentId,
                          option.options[index],
                          option,
                          comboSlotId
                        );
                      }}
                      price={priceForItemOptionModifier({ item, itemOption: option, modifier })}
                    />
                  );
                }
                // default to selector
                return option.options.map(optionModifier => {
                  const calories = getItemCalories(optionModifier);
                  const caloriesString = calories > 0 ? formatCalories(calories) : '';
                  const hasSelection = isModifierSelected(
                    optionModifier,
                    modifierSelections,
                    comboSlotId,
                    option
                  );
                  const modifierPrice = hasSelection
                    ? priceForItemOptionModifier({
                        item,
                        itemOption: option,
                        modifier: optionModifier,
                      })
                    : 0;
                  const optionModifierName = delv(optionModifier, 'name.locale');
                  return (
                    <MenuOptionSelector
                      comboSlotId={comboSlotId}
                      itemOption={option}
                      image={
                        optionModifier.image || optionModifier.modifierMultiplier.modifier.image
                      }
                      imageDescription={optionModifier?.imageDescription?.locale}
                      name={optionModifierName}
                      inputName={group.title}
                      subtitle={caloriesString}
                      key={optionModifierName}
                      value={optionModifier._key}
                      itemId={radioGroupParentId}
                      checked={hasSelection}
                      quantity={modifierSelectedQuantity(
                        optionModifier,
                        modifierSelections,
                        comboSlotId
                      )}
                      onQuantityUpdate={handleModifierQuantityUpdate}
                      onChange={() => {
                        handleModifierChange(
                          radioGroupParentId,
                          optionModifier,
                          option,
                          comboSlotId
                        );
                      }}
                      price={modifierPrice}
                      testId={`item-option-${optionModifierName}`}
                    />
                  );
                });
              })}
            </Form>
          </SelectionContainer>
        ) : null;
      })}
    </>
  );
}

export default Modifiers;
